import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * One organization in the hierarchy.
 *
 * The endpoint answers with a flat list; the hierarchy is carried by
 * `parent_organization_id` rather than by nesting. Read leniently — only the
 * id, the name and the parent pointer are used here, and everything else the
 * endpoint returns (address, source system, metadata) passes through untouched
 * so a field being added upstream cannot break the dropdown.
 */
const orgHierarchyNodeShape = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    parent_organization_id: z.string().nullish(),
    type: z.string().nullish(),
  })
  .passthrough();

export type OrgHierarchyNode = z.infer<typeof orgHierarchyNodeShape>;

const orgHierarchyShape = z.array(orgHierarchyNodeShape);

export interface OrgOption {
  orgId: string;
  orgName: string;
  /** How deep in the hierarchy, so the dropdown can indent descendants. */
  depth: number;
}

/**
 * Orders the flat list as a tree, depth-first.
 *
 * An organization whose parent is not itself in the list is treated as a root:
 * the response is scoped to what the admin can see, so a parent above that
 * scope is not missing data, it is the edge of it.
 *
 * Each organization is emitted at most once, so a parent cycle cannot recurse
 * forever — and anything a cycle leaves unreachable is appended flat rather
 * than dropped, since a missing organization is worse than a mis-indented one.
 */
export function flattenOrgHierarchy(orgs: OrgHierarchyNode[]): OrgOption[] {
  const ids = new Set(orgs.map((org) => org.id));

  const childrenByParent = new Map<string, OrgHierarchyNode[]>();
  const roots: OrgHierarchyNode[] = [];

  orgs.forEach((org) => {
    const parentId = org.parent_organization_id;
    if (parentId && ids.has(parentId)) {
      childrenByParent.set(parentId, [
        ...(childrenByParent.get(parentId) ?? []),
        org,
      ]);
    } else {
      roots.push(org);
    }
  });

  const emitted = new Set<string>();

  const walk = (nodes: OrgHierarchyNode[], depth: number): OrgOption[] =>
    nodes.flatMap((node) => {
      if (emitted.has(node.id)) return [];
      emitted.add(node.id);
      return [
        { orgId: node.id, orgName: node.name ?? node.id, depth },
        ...walk(childrenByParent.get(node.id) ?? [], depth + 1),
      ];
    });

  const ordered = walk(roots, 0);

  // Anything still unemitted is in a parent cycle, so it never had a root to be
  // reached from. Listing it flat is wrong-looking but visible; dropping it
  // would quietly remove an organization the admin is entitled to.
  const stranded = orgs.filter((org) => !emitted.has(org.id));

  return [...ordered, ...walk(stranded, 0)];
}

export interface UseOrgHierarchyParams {
  /** Narrows the hierarchy to organizations entitled to one application. */
  appCode?: string;
}

const getOrgHierarchy = async ({
  appCode,
}: UseOrgHierarchyParams): Promise<OrgHierarchyNode[]> => {
  const response = await fetcher('/composite/subscriptions/orgs/hierarchy', {
    service: '/fulfillment',
    version: '/v2',
    queryParameters: { appCode },
  });

  return orgHierarchyShape.parse(response);
};

export function orgHierarchyQuery(params: UseOrgHierarchyParams = {}) {
  return {
    queryKey: ['fulfillment-org-hierarchy', params.appCode ?? null],
    queryFn: () => getOrgHierarchy(params),
  };
}

/** The organizations an admin can filter by, ordered for a dropdown. */
export function useOrgHierarchy(params: UseOrgHierarchyParams = {}) {
  return useQuery({
    ...orgHierarchyQuery(params),
    select: flattenOrgHierarchy,
  });
}
